import { HealthProceduresTypes, IHealthProcedures, IHealthProceduresOmics } from '../healthProcedures'
import { theme } from '~/styles'
import { IconName } from '~/assets/icons'

export enum ERectangleVariantNames {
  OMICS = 'omics',
  CONDUCT = 'conduct',
  DIAGNOSIS = 'diagnosis'
}

export enum EMedicalViews {
  GENERAL = 'general',
  MATERNAL_INFANT = 'maternalInfant',
  RADIOLOGY = 'radiology',
  LABORATORY = 'laboratory',
  ONCOLOGY = 'oncology',
  NONE = 'none'
}

export enum EViewOptions {
  HUMAN_BODY = 'humanBody',
  TIMELINE = 'timeline'
}

export type MedicalViewsType = `${EMedicalViews}`
export type ViewsType = `${EViewOptions}`

export type TimelineMaternalInfantPeriodType = 'weekly'
export type TimelineGeneralPeriodType = 'days' | 'months' | 'years' | 'none'
export type TimelinePeriodTypes = TimelineGeneralPeriodType | TimelineMaternalInfantPeriodType

// TODO: substituir pelos tipos abaixo, onde for utilizado TimelineGeneralPeriodType e Cia.
// Motivo: TimelineGeneralPeriodType estava sendo utilizando tanto para General quanto para Radiology
export type Days = 'days'
export type Months = 'months'
export type Years = 'years'
export type None = 'none'
export type PeriodOfTime = Days | Months | Years | None

export interface ISelectedPeriod {
  period: PeriodOfTime
  previousPeriod: PeriodOfTime
}

export const yearsPeriod: Readonly<ISelectedPeriod> = {
  period: 'years',
  previousPeriod: 'none'
}

export const monthsPeriod: Readonly<ISelectedPeriod> = {
  period: 'months',
  previousPeriod: 'none'
}

export const daysPeriod: Readonly<ISelectedPeriod> = {
  period: 'days',
  previousPeriod: 'none'
}

export const createSelectedPeriodFrom = (newPeriod: PeriodOfTime | string): ISelectedPeriod => {
  switch (newPeriod) {
    case 'years':
      return yearsPeriod
    case 'months':
      return monthsPeriod
    case 'days':
      return daysPeriod
    default:
      return {} as ISelectedPeriod
  }
}

export const createSelectedPeriodFromPrevious = (previousPeriod: PeriodOfTime): ISelectedPeriod => {
  return { period: previousPeriod, previousPeriod: 'none' }
}

export const createDaysSelectedPeriodWithPrevious = (newPreviousPeriod: PeriodOfTime): ISelectedPeriod => {
  return { period: 'days', previousPeriod: newPreviousPeriod }
}

export const yearsPeriodWasSelected = (selectedPeriod: ISelectedPeriod): boolean => {
  return selectedPeriod.period === 'years'
}

export const monthsPeriodWasSelected = (selectedPeriod: ISelectedPeriod): boolean => {
  return selectedPeriod.period === 'months'
}

export const daysPeriodWasSelected = (selectedPeriod: ISelectedPeriod): boolean => {
  return selectedPeriod.period === 'days'
}

export interface ITimelineButtonsProps {
  color: string
  iconName: IconName
}

export type TimelineVariantProps<T extends string> = {
  [property in T]: ITimelineButtonsProps
}

export interface IOmicsPositioned extends IHealthProceduresOmics {
  percentagePosition: number
}

export interface IAdmissionStructured extends IAdmissionRowSlots {
  percentagePosition: number
  width: number
}

export interface ITimelineFiltersFunctor {
  value: IHealthProcedures[]
  perDate: (dates: Date[]) => ITimelineFiltersFunctor
  perEventStartDate: (dates: Date[]) => ITimelineFiltersFunctor
  perEventEndDate: (dates: Date[]) => ITimelineFiltersFunctor
  perType: (types: string[]) => ITimelineFiltersFunctor
  perTextTerm: (term: string) => ITimelineFiltersFunctor
  perMultipleTextTerms: (terms: string[]) => ITimelineFiltersFunctor
}

interface IRowSlots extends IHealthProcedures {}

export interface IAdmissionRowSlots extends IHealthProcedures {
  healthProcedures: IHealthProcedures[]
  isInProgress?: boolean
}

export interface IDiagnosisRowSlots extends IHealthProcedures {
  healthProcedures: IHealthProcedures[]
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export declare namespace ITimelineGeneral {
  interface IDailyRowSlotsWrapper {
    id: number
    startDate: string
    daysRowSlotsTotal: number
    rowSlots: IRowSlots[]
  }

  interface IDailyTimelineData {
    timelineId: number
    months: Array<{
      id: string
      date: string
      monthRowSlotsTotal: number
      days: IDailyRowSlotsWrapper[]
    }>
  }

  interface IMonthlyRowSlotsWrapper {
    id: string
    startDate: string
    monthsRowSlotsTotal: number
    rowSlots: IRowSlots[]
  }

  interface IMonthlyYearsSlotsWrapper {
    id: string
    yearsRowSlotsTotal: number
    months: IMonthlyRowSlotsWrapper[]
  }

  interface IMonthlyTimelineData {
    timelineId: number
    years: IMonthlyYearsSlotsWrapper[]
  }

  interface ISlot {
    type: HealthProceduresTypes
    healthProcedures: IHealthProcedures[]
  }

  interface IYearsRowSlotsWrapper {
    id: string
    date: string
    yearRowSlotsTotal: number
    rowSlots: ISlot[]
  }

  interface IYearTimelineData {
    timelineId: number
    years: IYearsRowSlotsWrapper[]
  }
}

type FilterTagType = 'admissionEvent' | 'searchTerm' | 'dateFilter' | 'typeFilter'
export interface IFilterTag {
  id: string
  text: string
  color: string
  type: FilterTagType
}

export const createAdmissionEventTagWith = (description: string): Readonly<IFilterTag> => {
  if (!description || description.trim() === '') return {} as Readonly<IFilterTag>

  return {
    id: '0-ft',
    type: 'admissionEvent',
    text: description,
    color: theme.colors.colorC58A3A
  }
}

export const createSearchTermTagWith = (description: string): Readonly<IFilterTag> => {
  if (!description || description.trim() === '') return {} as Readonly<IFilterTag>

  return {
    id: description,
    type: 'searchTerm',
    text: description,
    color: theme.colors.color005597
  }
}

export const createDateFilterTagWith = (description: string): Readonly<IFilterTag> => {
  if (!description || description.trim() === '') return {} as Readonly<IFilterTag>

  return {
    id: '2-ft',
    type: 'dateFilter',
    text: description,
    color: theme.colors.color378ff7
  }
}

export const createTypeFiltersTagWith = (description: string, color: string, types: string[]): Readonly<IFilterTag> => {
  if (!description || description.trim() === '') return {} as Readonly<IFilterTag>

  return {
    id: types.join('-'),
    type: 'typeFilter',
    text: description,
    color: color || theme.colors.color8191a5
  }
}

export * from './timelineMaternalInfant'
